import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { flushSync } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { toast } from 'sonner';
import { sanitizeMarkdown } from '@/features/chat/shared';
import {
  FileChip,
  ChatInput,
  FileSelector,
} from '@/features/chat/shared/components';
import { ChatContainer } from '@/features/chat/shared/components/ChatContainer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { Plus, Loader, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

import { RootState } from '../../../reducers';
import { MultiFileChatProps, Stage, LoadingStage } from './types';

import useLimits from '../../plan/hooks/limits';
import {
  getMultiFileSessions,
  deleteMultiFileSession,
  askMultiFileQuestionStream,
} from '../../../services/multiFileAskAIService';
import { screenAppAPIClient } from '../../../lib/baseAxios';

import { useUserTags } from '../../../hooks/useTags';
import { useOutputLanguage } from '../../../hooks/useOutputLanguage';
import { FileType as GlobalFileType } from '../../../types';
import ChatMessages from './ChatMessages';
import {
  trackAskAIChat,
  type AskAIChatType,
} from '../shared/chatTrackingActions';

// Saved conversation structure for history (matches backend SessionListItem)
interface SavedConversation {
  _id: string;
  fileIds: string[];
  conversation: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
  }>;
  createdAt: string;
  updatedAt: string;
  teamId?: string;
}

export interface MultiFileChatRef {
  clearChat: () => void;
  hasContent: () => boolean;
  addFile: (file: GlobalFileType) => void;
  removeFile: (fileId: string) => void;
  focusInput: () => void;
}

const MultiFileChat = forwardRef<MultiFileChatRef, MultiFileChatProps>(
  (
    {
      open = false,
      externalShowHistoryDropdown,
      externalSetShowHistoryDropdown,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [stage, setStage] = useState<Stage>('initial');
    const [loadingStage, setLoadingStage] = useState<LoadingStage>(null);
    const [cachedFiles, setCachedFiles] = useState<GlobalFileType[]>([]);
    const [showFileDropdown, setShowFileDropdown] = useState(false);
    const [internalShowHistoryDropdown, setInternalShowHistoryDropdown] =
      useState(false);
    const [showSelectedFilesDropdown, setShowSelectedFilesDropdown] =
      useState(false);

    // Use external history dropdown state if provided, otherwise use internal
    const showHistoryDropdown =
      externalShowHistoryDropdown ?? internalShowHistoryDropdown;
    const setShowHistoryDropdown =
      externalSetShowHistoryDropdown ?? setInternalShowHistoryDropdown;

    const team = useSelector((state: RootState) => state.team.selectedTeam);

    const guardLimits = useLimits();
    const [directions, setDirections] = useState('');
    const { getTag, setTag } = useUserTags();
    // Selected files - will be populated from session on mount
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
    const { currentLanguageName } = useOutputLanguage();
    const [copySuccess, setCopySuccess] = useState(false);
    const [resultSearchTerm, setResultSearchTerm] = useState('');
    const [savedConversations, setSavedConversations] = useState<
      SavedConversation[]
    >([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [completion, setCompletion] = useState<string>(
      getTag('multi-ask-ai-last-output') || '',
    );
    const [isLoading, setIsLoading] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(
      getTag('multi-ask-ai-session-id') || null,
    );
    const [conversationHistory, setConversationHistory] = useState<
      Array<{ role: 'user' | 'assistant'; content: string }>
    >(() => {
      try {
        const savedHistory = getTag('multi-ask-ai-history');
        return savedHistory ? JSON.parse(savedHistory as string) : [];
      } catch {
        return [];
      }
    });
    const [isFileDragging, setIsFileDragging] = useState(false);
    const [isLoadingFromHistory, setIsLoadingFromHistory] = useState(false);
    const [isSearchingFiles, setIsSearchingFiles] = useState(false);

    // Track if user explicitly cleared chat to prevent auto-loading
    // Initialize from localStorage
    const clearedValue = localStorage.getItem('multi-ask-ai-cleared');
    const userClearedChatRef = useRef(clearedValue === 'true');

    // AbortController ref for stopping streaming responses
    const abortControllerRef = useRef<AbortController | null>(null);

    // Track accumulated response during streaming so we can save partial response on abort
    const streamingResponseRef = useRef<string>('');

    // Ref for external history dropdown click-outside detection
    const externalHistoryDropdownRef = useRef<HTMLDivElement>(null);

    // Ref for accessing the input textarea
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Global drag detection to show hint in empty state
    useEffect(() => {
      const handleGlobalDragStart = (e: DragEvent) => {
        // Check if this is a file drag from our library
        if (e.dataTransfer?.types.includes('application/json')) {
          setIsFileDragging(true);
        }
      };

      const handleGlobalDragEnd = () => {
        setIsFileDragging(false);
      };

      document.addEventListener('dragstart', handleGlobalDragStart);
      document.addEventListener('dragend', handleGlobalDragEnd);
      document.addEventListener('drop', handleGlobalDragEnd);

      return () => {
        document.removeEventListener('dragstart', handleGlobalDragStart);
        document.removeEventListener('dragend', handleGlobalDragEnd);
        document.removeEventListener('drop', handleGlobalDragEnd);
      };
    }, []);

    // Note: We don't persist selectedFiles or conversationHistory separately
    // The session ID is the single source of truth, and all state is restored from the session

    // Handle click outside for external history dropdown
    useEffect(() => {
      if (!showHistoryDropdown || !externalShowHistoryDropdown)
        return undefined;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          externalHistoryDropdownRef.current &&
          !externalHistoryDropdownRef.current.contains(event.target as Node) &&
          !(event.target as Element).closest('[data-history-trigger]')
        ) {
          setShowHistoryDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [
      showHistoryDropdown,
      externalShowHistoryDropdown,
      setShowHistoryDropdown,
    ]);

    const showToast = useCallback(
      (message: string, variant: 'default' | 'destructive' = 'default') => {
        if (variant === 'destructive') {
          toast.error(message);
        } else {
          toast(message);
        }
      },
      [],
    );

    // Track if initial load has completed to prevent re-loading on session refresh
    const hasLoadedInitialSessionRef = useRef(false);

    // Clear chat function - clears session and starts fresh
    const clearChat = useCallback(() => {
      // Mark that user explicitly cleared the chat and persist it
      userClearedChatRef.current = true;
      localStorage.setItem('multi-ask-ai-cleared', 'true');
      // Mark that initial load is done (prevents auto-load from running again)
      hasLoadedInitialSessionRef.current = true;

      setCompletion('');
      setStage('initial');
      setDirections('');
      setSelectedFiles([]);
      setCachedFiles([]);
      setCurrentSessionId(null);
      setConversationHistory([]);
      // Clear the session ID - this is the key that prevents loading old data
      setTag({
        key: 'multi-ask-ai-session-id',
        value: '',
      });
      // Also clear the last output
      setTag({
        key: 'multi-ask-ai-last-output',
        value: '',
      });

      // Focus the input after clearing
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }, [setTag]);

    // Expose clearChat, hasContent, addFile, removeFile, and focusInput methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        clearChat,
        hasContent: () => {
          return (
            conversationHistory.length > 0 ||
            completion.length > 0 ||
            selectedFiles.length > 0
          );
        },
        addFile: (file: GlobalFileType) => {
          const fileId = file._id;
          setSelectedFiles((prev) => {
            if (prev.includes(fileId)) {
              // File already selected, don't add it again
              return prev;
            }
            // Add to cache since it's newly selected
            setCachedFiles((current) => [...current, file]);
            // Add to selected files
            return [...prev, fileId];
          });
        },
        removeFile: (fileId: string) => {
          setSelectedFiles((prev) => {
            if (!prev.includes(fileId)) {
              // File not selected, nothing to remove
              return prev;
            }
            // Remove from cache
            setCachedFiles((current) =>
              current.filter((f) => f._id !== fileId),
            );
            // Remove from selected files
            return prev.filter((id) => id !== fileId);
          });
        },
        focusInput: () => {
          // Use setTimeout to ensure DOM has updated before focusing
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        },
      }),
      [
        clearChat,
        conversationHistory.length,
        completion.length,
        selectedFiles.length,
      ],
    );

    // Stop the currently running generation
    const stopGeneration = useCallback(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;

        // Save the partial response to conversation history
        const partialResponse = streamingResponseRef.current;
        if (partialResponse) {
          const sanitizedCompletion = sanitizeMarkdown(partialResponse);

          // Add assistant response to conversation history
          setConversationHistory((prev) => [
            ...prev,
            { role: 'assistant', content: sanitizedCompletion },
          ]);

          // Update completion state
          setCompletion(sanitizedCompletion);

          // Persist to tags
          setTag({
            key: 'multi-ask-ai-last-output',
            value: sanitizedCompletion,
          });

          // Clear the streaming ref
          streamingResponseRef.current = '';
        }

        setIsLoading(false);
        setLoadingStage(null);
        setStage('final');
      }
    }, [setTag]);

    // Load saved conversations from backend on mount
    useEffect(() => {
      const fetchSessions = async () => {
        if (!team?.teamId) return;

        setIsLoadingSessions(true);
        try {
          const response = await getMultiFileSessions(1, 50);
          if (response.success && response.data) {
            setSavedConversations(response.data.sessions);
          }
        } catch (error) {
          console.error('Failed to fetch conversation history:', error);
          showToast('Failed to load conversation history', 'destructive');
        } finally {
          setIsLoadingSessions(false);
        }
      };

      fetchSessions();
    }, [team?.teamId]);

    // Load previous session on mount if it exists
    useEffect(() => {
      // Don't run until conversations are loaded
      if (savedConversations.length === 0) {
        return;
      }

      // CRITICAL: Only run this effect ONCE on initial load (when no currentSessionId exists)
      // This prevents re-loading random sessions when savedConversations updates
      // BUT we allow it to run when we have a currentSessionId to sync with backend data
      if (hasLoadedInitialSessionRef.current && !currentSessionId) {
        return;
      }

      if (userClearedChatRef.current && !currentSessionId) {
        hasLoadedInitialSessionRef.current = true;
        return;
      }

      let conversationToLoad: SavedConversation | undefined;

      // First, try to load the session with the stored session ID
      if (currentSessionId) {
        conversationToLoad = savedConversations.find(
          (conv) => conv._id === currentSessionId,
        );

        if (!conversationToLoad) {
          // Session ID exists but session not found - clear it
          setCurrentSessionId(null);
          setTag({
            key: 'multi-ask-ai-session-id',
            value: '',
          });
          hasLoadedInitialSessionRef.current = true;
          // Don't fall back to most recent - user likely deleted this session
          return;
        }
      }

      // ONLY load the most recent conversation if we have no session ID AND the user didn't explicitly clear
      // This prevents auto-loading after user clicks "New Chat"
      if (
        !conversationToLoad &&
        !currentSessionId &&
        savedConversations.length > 0
      ) {
        // This should only happen on first load with no prior session
        [conversationToLoad] = savedConversations; // Sessions are already sorted by updatedAt desc
      }

      // If we have a conversation to load, restore it
      if (conversationToLoad) {
        // Set flag to prevent animations when loading from history
        setIsLoadingFromHistory(true);

        // Restore the full conversation history from the session
        setConversationHistory(conversationToLoad.conversation);

        // Find the last assistant response and set it as the completion
        const assistantMessage = conversationToLoad.conversation
          .slice()
          .reverse()
          .find((msg) => msg.role === 'assistant');

        if (assistantMessage) {
          setCompletion(assistantMessage.content);
        }

        // Set selected files from session
        setSelectedFiles(conversationToLoad.fileIds);
        setStage('final');

        // Update the current session ID if it wasn't set
        if (!currentSessionId) {
          setCurrentSessionId(conversationToLoad._id);
          setTag({
            key: 'multi-ask-ai-session-id',
            value: conversationToLoad._id,
          });
        }

        // Fetch full file data for the session's files
        const fetchFilesData = async () => {
          try {
            const filePromises = conversationToLoad!.fileIds.map(
              async (fileId) => {
                try {
                  const response = await (screenAppAPIClient as any).api.get(
                    `/v2/files/${fileId}`,
                  );
                  return (
                    response.data?.data?.file ||
                    response.data?.file ||
                    response.data
                  );
                } catch (error) {
                  console.error(`Failed to fetch file ${fileId}:`, error);
                  return null;
                }
              },
            );
            const filesData = await Promise.all(filePromises);
            const validFiles = filesData.filter(Boolean);
            setCachedFiles(validFiles);
          } catch (error) {
            console.error('Failed to fetch files data:', error);
            setCachedFiles([]);
          }
        };

        fetchFilesData();
      }

      // Mark that we've completed initial load
      hasLoadedInitialSessionRef.current = true;
    }, [savedConversations, setTag]);

    // Refresh sessions list from backend
    const refreshSessions = useCallback(async () => {
      if (!team?.teamId) return;

      try {
        const response = await getMultiFileSessions(1, 50);
        if (response.success && response.data) {
          setSavedConversations(response.data.sessions);
        }
      } catch (error) {
        console.error('Failed to refresh conversation history:', error);
      }
    }, [team?.teamId]);

    // Note: Session tracking is handled by backend with the summarized endpoint
    // No need for client-side session management

    // Load a conversation from history
    const loadConversation = useCallback(
      (conversationId: string) => {
        const conversationToLoad = savedConversations.find(
          (conv) => conv._id === conversationId,
        );
        if (conversationToLoad) {
          // Reset the clear flag when manually loading a conversation and persist it
          userClearedChatRef.current = false;
          localStorage.setItem('multi-ask-ai-cleared', 'false');
          // Mark that we've loaded a session (prevents auto-load effect from running)
          hasLoadedInitialSessionRef.current = true;

          // Set flag to prevent animations when loading from history
          setIsLoadingFromHistory(true);

          // Restore the full conversation history
          setConversationHistory(conversationToLoad.conversation);

          // Restore the session ID for continuing the conversation
          setCurrentSessionId(conversationId);
          setTag({
            key: 'multi-ask-ai-session-id',
            value: conversationId,
          });

          // Find the last user message and assistant response
          const userMessage = conversationToLoad.conversation.find(
            (msg) => msg.role === 'user',
          );
          const assistantMessage = conversationToLoad.conversation
            .slice()
            .reverse()
            .find((msg) => msg.role === 'assistant');

          if (userMessage) {
            setDirections('');
          }

          if (assistantMessage) {
            // Backend now returns citations in correct markdown format [→](fileId@timestamp)
            setCompletion(assistantMessage.content);
          }

          setSelectedFiles(conversationToLoad.fileIds);
          setStage('final');

          // Fetch full file data for the conversation's files
          const fetchFilesData = async () => {
            try {
              const filePromises = conversationToLoad.fileIds.map(
                async (fileId) => {
                  try {
                    const response = await (screenAppAPIClient as any).api.get(
                      `/v2/files/${fileId}`,
                    );
                    return (
                      response.data?.data?.file ||
                      response.data?.file ||
                      response.data
                    );
                  } catch (error) {
                    console.error(`Failed to fetch file ${fileId}:`, error);
                    return null;
                  }
                },
              );
              const filesData = await Promise.all(filePromises);
              const validFiles = filesData.filter(Boolean);
              setCachedFiles(validFiles);
            } catch (error) {
              console.error('Failed to fetch files data:', error);
              setCachedFiles([]);
            }
          };
          fetchFilesData();

          setShowHistoryDropdown(false);
        }
      },
      [savedConversations, setTag, setShowHistoryDropdown],
    );

    // Delete a conversation from history
    const deleteConversation = useCallback(
      async (conversationId: string, e?: React.MouseEvent) => {
        e?.stopPropagation();

        try {
          await deleteMultiFileSession(conversationId);
          // Remove from local state immediately for better UX
          setSavedConversations((prev) =>
            prev.filter((conv) => conv._id !== conversationId),
          );
          showToast('Session deleted successfully');
        } catch (error) {
          console.error('Failed to delete conversation:', error);
          showToast('Failed to delete session', 'destructive');
        }
      },
      [],
    );

    const runAskAI = async (message?: string) => {
      // Reset the clear flag when user starts a new conversation and persist it
      userClearedChatRef.current = false;
      localStorage.setItem('multi-ask-ai-cleared', 'false');

      // Clear the history loading flag since this is a fresh user action
      setIsLoadingFromHistory(false);

      try {
        await guardLimits('multipleRecordingAskAI', async () => {
          // Use provided message or fall back to directions state
          const currentDirections = (message || directions).trim();

          // Use selected files (can be empty - backend will auto-search)
          const filesToUse = selectedFiles;

          // Clear input and show thinking state
          setDirections('');
          setStage('processing');
          setCompletion('');
          setLoadingStage('response');
          setIsLoading(true);

          try {
            if (!team?.teamId) {
              throw new Error('Team ID is required');
            }

            // Add user message to conversation history
            setConversationHistory((prev) => [
              ...prev,
              { role: 'user', content: currentDirections },
            ]);

            // Track Ask AI chat
            const chatType: AskAIChatType =
              filesToUse.length > 0 ? 'multifile' : 'search';

            dispatch(
              trackAskAIChat({
                chatType,
                question: currentDirections,
                fileCount: filesToUse.length,
                sessionId: currentSessionId,
                isNewSession: !currentSessionId,
              }),
            );

            // Create AbortController for cancellation
            abortControllerRef.current = new AbortController();

            // Clear previous streaming response
            streamingResponseRef.current = '';

            // Use streaming endpoint for real-time responses
            let fullResponse = '';
            const { sessionId, selectedFileIds } =
              await askMultiFileQuestionStream(
                team.teamId,
                {
                  fileIds: filesToUse,
                  question: currentDirections,
                  sessionId: currentSessionId ?? undefined,
                  searchLimit: 5,
                },
                (accumulatedText: string) => {
                  // Service sends accumulated text, just display it
                  fullResponse = accumulatedText;
                  // Store in ref so we can save partial response if aborted
                  streamingResponseRef.current = accumulatedText;
                  // Use flushSync to force immediate render for smooth streaming animation
                  flushSync(() => {
                    setCompletion(accumulatedText);
                    // Transition to final stage as soon as we receive first chunk
                    setStage('final');
                  });
                },
                { signal: abortControllerRef.current.signal },
              );

            // If files were auto-selected by backend, update UI
            if (selectedFileIds && selectedFileIds.length > 0) {
              console.log('Backend auto-selected files:', selectedFileIds);
              setSelectedFiles(selectedFileIds);

              // Fetch file metadata for auto-selected files
              const fetchAutoSelectedFiles = async () => {
                try {
                  console.log(
                    '[AutoSearch] Fetching metadata for',
                    selectedFileIds.length,
                    'files',
                  );
                  const filePromises = selectedFileIds.map(async (fileId) => {
                    try {
                      const response = await (
                        screenAppAPIClient as any
                      ).api.get(`/v2/files/${fileId}`);
                      return (
                        response.data?.data?.file ||
                        response.data?.file ||
                        response.data
                      );
                    } catch (error) {
                      console.error(
                        `[AutoSearch] Failed to fetch file ${fileId}:`,
                        error,
                      );
                      return null;
                    }
                  });
                  const filesData = await Promise.all(filePromises);
                  const validFiles = filesData.filter(Boolean);
                  console.log(
                    '[AutoSearch] Fetched',
                    validFiles.length,
                    'files',
                  );
                  setCachedFiles(validFiles);
                } catch (error) {
                  console.error(
                    '[AutoSearch] Failed to fetch files data:',
                    error,
                  );
                }
              };
              fetchAutoSelectedFiles();
            }

            // Clear the abort controller after successful completion
            abortControllerRef.current = null;

            // Clear the streaming ref after successful completion
            streamingResponseRef.current = '';

            // Sanitize the complete response
            const sanitizedCompletion = sanitizeMarkdown(fullResponse);
            setCompletion(sanitizedCompletion);

            // Store the session ID for conversation continuity
            setCurrentSessionId(sessionId);
            setTag({
              key: 'multi-ask-ai-session-id',
              value: sessionId,
            });

            // Add assistant response to conversation history
            setConversationHistory((prev) => [
              ...prev,
              { role: 'assistant', content: sanitizedCompletion },
            ]);

            setStage('final');
            setTag({
              key: 'multi-ask-ai-last-output',
              value: sanitizedCompletion,
            });

            // Refresh sessions to show the new conversation
            await refreshSessions();
          } catch (error) {
            console.error('Failed to analyze recordings:', error);

            // Clear the abort controller
            abortControllerRef.current = null;

            // Check if error was due to abort
            if (error instanceof Error && error.name === 'AbortError') {
              // User cancelled - don't show error toast
              console.log('Request was cancelled by user');
            } else {
              setStage('initial');
              showToast(
                error instanceof Error
                  ? error.message
                  : t(
                      'drawer.multiAskAI.failedToProcessTranscripts',
                      'Failed to process transcripts. Please try again.',
                    ),
                'destructive',
              );
            }
          } finally {
            setLoadingStage(null);
            setIsLoading(false);
          }
        });
      } catch (error) {
        console.log('Guard limits reached:', error);
        setLoadingStage(null);
        setIsSearchingFiles(false);
        setDirections('');
      }
    };

    const handleFileSelect = (file: GlobalFileType) => {
      const fileId = file._id;
      setSelectedFiles((prev) => {
        const newSelection = prev.includes(fileId)
          ? prev.filter((id) => id !== fileId)
          : [...prev, fileId];

        // Update cachedFiles based on the new selection
        if (prev.includes(fileId)) {
          // Remove from cache if deselected
          setCachedFiles((current) => current.filter((f) => f._id !== fileId));
          // Close the selected files dropdown if this was the last file
          if (prev.length === 1) {
            setShowSelectedFilesDropdown(false);
          }
        } else {
          // Add to cache if newly selected with full file object
          setCachedFiles((current) => [...current, file]);

          // Keep the file dropdown open when a file is added
          // User can close it manually or click outside to dismiss
        }

        return newSelection;
      });
    };

    const handleCopyContent = (cleanedText: string): void => {
      if (cleanedText) {
        navigator.clipboard.writeText(cleanedText);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    };

    // Drag and drop handlers for accepting files
    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        const fileData = e.dataTransfer.getData('application/json');
        if (fileData) {
          const file: GlobalFileType = JSON.parse(fileData);
          const fileId = file._id;

          // Only add the file if it's not already selected
          setSelectedFiles((prev) => {
            if (prev.includes(fileId)) {
              // File already selected, don't do anything
              return prev;
            }

            // Add to cache since it's newly selected
            setCachedFiles((current) => [...current, file]);

            // Focus the input after dropping a file
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);

            // Add to selected files
            return [...prev, fileId];
          });
        }
      } catch (error) {
        console.error('Failed to parse dropped file data:', error);
      }
    }, []);

    const handleEditQuery = async (
      messageIndex: number,
      newContent: string,
    ) => {
      // Find the user message at the given index
      const userMessage = conversationHistory[messageIndex];

      if (userMessage && userMessage.role === 'user') {
        // Truncate conversation history at this point (remove this message and all after)
        const truncatedHistory = conversationHistory.slice(0, messageIndex);
        setConversationHistory(truncatedHistory);

        // Update persisted history
        setTag({
          key: 'multi-ask-ai-history',
          value: JSON.stringify(truncatedHistory),
        });

        // Clear the completion to show we're processing
        setCompletion('');

        // Set loading state
        setStage('processing');
        setLoadingStage('response');
        setIsLoading(true);

        try {
          if (!team?.teamId) {
            throw new Error('Team ID is required');
          }

          // Add the edited user message to conversation history
          setConversationHistory((prev) => [
            ...prev,
            { role: 'user', content: newContent },
          ]);

          // Create AbortController for cancellation
          abortControllerRef.current = new AbortController();

          // Clear previous streaming response
          streamingResponseRef.current = '';

          // Use streaming endpoint for real-time responses
          let fullResponse = '';
          const { sessionId, selectedFileIds } =
            await askMultiFileQuestionStream(
              team.teamId,
              {
                fileIds: selectedFiles,
                question: newContent,
                sessionId: currentSessionId ?? undefined,
                searchLimit: 5,
              },
              (accumulatedText: string) => {
                // Service sends accumulated text, just display it
                fullResponse = accumulatedText;
                // Store in ref so we can save partial response if aborted
                streamingResponseRef.current = accumulatedText;
                // Use flushSync to force immediate render for smooth streaming animation
                flushSync(() => {
                  setCompletion(accumulatedText);
                  // Transition to final stage as soon as we receive first chunk
                  setStage('final');
                });
              },
              { signal: abortControllerRef.current.signal },
            );

          // If files were auto-selected by backend, update UI
          if (selectedFileIds && selectedFileIds.length > 0) {
            console.log('Backend auto-selected files:', selectedFileIds);
            setSelectedFiles(selectedFileIds);

            // Fetch file metadata for auto-selected files
            const fetchAutoSelectedFiles = async () => {
              try {
                console.log(
                  '[AutoSearch] Fetching metadata for',
                  selectedFileIds.length,
                  'files',
                );
                const filePromises = selectedFileIds.map(async (fileId) => {
                  try {
                    const response = await (screenAppAPIClient as any).api.get(
                      `/v2/files/${fileId}`,
                    );
                    return (
                      response.data?.data?.file ||
                      response.data?.file ||
                      response.data
                    );
                  } catch (error) {
                    console.error(
                      `[AutoSearch] Failed to fetch file ${fileId}:`,
                      error,
                    );
                    return null;
                  }
                });
                const filesData = await Promise.all(filePromises);
                const validFiles = filesData.filter(Boolean);
                console.log('[AutoSearch] Fetched', validFiles.length, 'files');
                setCachedFiles(validFiles);
              } catch (error) {
                console.error(
                  '[AutoSearch] Failed to fetch files data:',
                  error,
                );
              }
            };
            fetchAutoSelectedFiles();
          }

          // Clear the abort controller after successful completion
          abortControllerRef.current = null;

          // Clear the streaming ref after successful completion
          streamingResponseRef.current = '';

          // Sanitize the complete response
          const sanitizedCompletion = sanitizeMarkdown(fullResponse);
          setCompletion(sanitizedCompletion);

          // Store the session ID for conversation continuity
          setCurrentSessionId(sessionId);
          setTag({
            key: 'multi-ask-ai-session-id',
            value: sessionId,
          });

          // Add assistant response to conversation history
          setConversationHistory((prev) => [
            ...prev,
            { role: 'assistant', content: sanitizedCompletion },
          ]);

          setStage('final');
          setTag({
            key: 'multi-ask-ai-last-output',
            value: sanitizedCompletion,
          });

          // Refresh sessions to show the updated conversation
          await refreshSessions();
        } catch (error) {
          console.error('Failed to analyze recordings:', error);

          // Clear the abort controller
          abortControllerRef.current = null;

          // Check if error was due to abort
          if (error instanceof Error && error.name === 'AbortError') {
            // User cancelled - don't show error toast
            console.log('Request was cancelled by user');
          } else {
            setStage('initial');
            showToast(
              error instanceof Error
                ? error.message
                : t(
                    'drawer.multiAskAI.failedToProcessTranscripts',
                    'Failed to process transcripts. Please try again.',
                  ),
              'destructive',
            );
          }
        } finally {
          setLoadingStage(null);
          setIsLoading(false);
        }
      }
    };

    const handleRegenerateResponse = async (messageIndex: number) => {
      // Find the assistant message at the given index
      const assistantMessage = conversationHistory[messageIndex];

      if (assistantMessage && assistantMessage.role === 'assistant') {
        // Find the corresponding user message (should be right before this assistant message)
        const userMessageIndex = messageIndex - 1;
        const userMessage = conversationHistory[userMessageIndex];

        if (userMessage && userMessage.role === 'user') {
          // Truncate conversation history at the assistant message (keeping the user message)
          const truncatedHistory = conversationHistory.slice(0, messageIndex);

          // Use flushSync to ensure state updates happen immediately before streaming starts
          flushSync(() => {
            setConversationHistory(truncatedHistory);
            // Clear the completion to show we're processing
            setCompletion('');
            // Set loading state
            setStage('processing');
            setLoadingStage('response');
            setIsLoading(true);
          });

          // Update persisted history
          setTag({
            key: 'multi-ask-ai-history',
            value: JSON.stringify(truncatedHistory),
          });

          try {
            if (!team?.teamId) {
              throw new Error('Team ID is required');
            }

            // Create AbortController for cancellation
            abortControllerRef.current = new AbortController();

            // Clear previous streaming response
            streamingResponseRef.current = '';

            // Use streaming endpoint with the original user message
            let fullResponse = '';
            const { sessionId } = await askMultiFileQuestionStream(
              team.teamId,
              {
                fileIds: selectedFiles,
                question: userMessage.content,
                sessionId: currentSessionId ?? undefined,
              },
              (accumulatedText: string) => {
                // Service sends accumulated text, just display it
                fullResponse = accumulatedText;
                // Store in ref so we can save partial response if aborted
                streamingResponseRef.current = accumulatedText;
                // Use flushSync to force immediate render for smooth streaming animation
                flushSync(() => {
                  setCompletion(accumulatedText);
                  // Transition to final stage as soon as we receive first chunk
                  setStage('final');
                });
              },
              { signal: abortControllerRef.current.signal },
            );

            // Clear the abort controller after successful completion
            abortControllerRef.current = null;

            // Clear the streaming ref after successful completion
            streamingResponseRef.current = '';

            // Sanitize the complete response
            const sanitizedCompletion = sanitizeMarkdown(fullResponse);
            setCompletion(sanitizedCompletion);

            // Store the session ID for conversation continuity
            setCurrentSessionId(sessionId);
            setTag({
              key: 'multi-ask-ai-session-id',
              value: sessionId,
            });

            // Add new assistant response to conversation history
            setConversationHistory((prev) => [
              ...prev,
              { role: 'assistant', content: sanitizedCompletion },
            ]);

            setStage('final');
            setTag({
              key: 'multi-ask-ai-last-output',
              value: sanitizedCompletion,
            });

            // Refresh sessions to show the updated conversation
            await refreshSessions();
          } catch (error) {
            console.error('Failed to regenerate response:', error);

            // Clear the abort controller
            abortControllerRef.current = null;

            // Check if error was due to abort
            if (error instanceof Error && error.name === 'AbortError') {
              // User cancelled - don't show error toast
              console.log('Request was cancelled by user');
            } else {
              setStage('initial');
              showToast(
                error instanceof Error
                  ? error.message
                  : t(
                      'drawer.multiAskAI.failedToProcessTranscripts',
                      'Failed to regenerate response. Please try again.',
                    ),
                'destructive',
              );
            }
          } finally {
            setLoadingStage(null);
            setIsLoading(false);
          }
        }
      }
    };

    const renderInputArea = () => {
      const placeholder =
        selectedFiles.length > 0
          ? t(
              'drawer.multiAskAI.searchPlaceholder.withFiles',
              `Ask anything about these recordings... (Output: ${currentLanguageName})`,
            )
          : t(
              'drawer.multiAskAI.searchPlaceholder.noFiles',
              `Ask anything... (Output: ${currentLanguageName})`,
            );

      // Determine chips to display
      let chips: Array<{ element: React.ReactElement }> = [];
      if (isSearchingFiles) {
        chips = [
          {
            element: (
              <FileChip
                fileCount={0}
                fileName="Searching"
                isLoading
                onClick={() => {}}
                onClear={() => {}}
              />
            ),
          },
        ];
      } else if (selectedFiles.length > 0) {
        chips = [
          {
            element: (
              <FileChip
                fileCount={selectedFiles.length}
                fileName={cachedFiles[0]?.name}
                isLoading={selectedFiles.length > 0 && cachedFiles.length === 0}
                onClick={() =>
                  setShowSelectedFilesDropdown(!showSelectedFilesDropdown)
                }
                onClear={() => {
                  setSelectedFiles([]);
                  setCachedFiles([]);
                  setShowSelectedFilesDropdown(false);
                }}
              />
            ),
          },
        ];
      }

      return (
        <ChatInput
          value={directions}
          onChange={setDirections}
          onSubmit={runAskAI}
          placeholder={placeholder}
          disabled={stage === 'processing'}
          disableSend={false}
          isLoading={isLoading}
          loadingIcon="square"
          onStopLoading={stopGeneration}
          inputRef={inputRef}
          chips={chips}
          leftButton={{
            icon: <Plus className="h-4 w-4 text-foreground" />,
            onClick: () => {
              // Close history dropdown if it's open
              if (showHistoryDropdown) {
                setShowHistoryDropdown(false);
              }
              setShowFileDropdown(!showFileDropdown);
            },
            ariaLabel: 'Add context',
          }}
          inactiveOpacity={false}
          enableDictation
          autoSendOnDictation
        >
          {/* File Dropdown menu - positioned above the button */}
          <Popover open={showFileDropdown} onOpenChange={setShowFileDropdown}>
            <PopoverTrigger asChild>
              <span />
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              sideOffset={8}
              alignOffset={0}
              collisionPadding={16}
              className="p-0 w-auto border-0 shadow-none"
            >
              <FileSelector
                selectedFiles={selectedFiles}
                onFileSelect={handleFileSelect}
              />
            </PopoverContent>
          </Popover>

          {/* History Dropdown menu - positioned based on trigger location */}
          {showHistoryDropdown && externalShowHistoryDropdown && (
            <div
              ref={externalHistoryDropdownRef}
              className="fixed top-14 right-6 z-[4100]"
            >
              <div className="w-96 max-h-96 overflow-hidden bg-popover border border-border rounded-md shadow-md animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200">
                {/* Header section matching the + menu style */}
                <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium">
                    {t('drawer.multiAskAI.recentSessions', 'Recent Sessions')}
                  </h3>
                </div>

                {/* Content area in ScrollArea */}
                <ScrollArea className="h-64">
                  {(() => {
                    if (isLoadingSessions) {
                      return (
                        <div className="flex items-center justify-center py-8">
                          <Loader className="animate-spin h-5 w-5 text-primary" />
                        </div>
                      );
                    }

                    if (savedConversations.length === 0) {
                      return (
                        <div className="flex items-center justify-center h-full p-8">
                          <p className="text-sm text-muted-foreground text-center">
                            {t(
                              'drawer.multiAskAI.noHistory',
                              'No history available yet',
                            )}
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div>
                        {savedConversations.map((conv) => {
                          // Get the first user message as the title
                          const firstUserMsg = conv.conversation.find(
                            (msg) => msg.role === 'user',
                          );
                          const title =
                            firstUserMsg?.content.slice(0, 50) || 'Untitled';

                          const timeAgo = (() => {
                            const diff =
                              Date.now() - new Date(conv.updatedAt).getTime();
                            const minutes = Math.floor(diff / 60000);
                            const hours = Math.floor(diff / 3600000);
                            const days = Math.floor(diff / 86400000);

                            if (minutes < 1) return 'Just now';
                            if (minutes < 60) return `${minutes}m ago`;
                            if (hours < 24) return `${hours}h ago`;
                            return `${days}d ago`;
                          })();

                          return (
                            <button
                              key={conv._id}
                              type="button"
                              onClick={() => loadConversation(conv._id)}
                              className="w-full text-left group flex items-center gap-3 px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors relative"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-sm mb-1 line-clamp-1 font-normal">
                                  {title}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>
                                    {conv.fileIds.length}{' '}
                                    {conv.fileIds.length === 1
                                      ? 'file'
                                      : 'files'}
                                  </span>
                                  <span>•</span>
                                  <span>{timeAgo}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => deleteConversation(conv._id, e)}
                                className="opacity-0 group-hover:opacity-70 hover:opacity-100 transition-opacity"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </ScrollArea>
              </div>
            </div>
          )}

          {/* History Dropdown menu - when triggered from input area (internal) */}
          {showHistoryDropdown && !externalShowHistoryDropdown && (
            <Popover
              open={showHistoryDropdown}
              onOpenChange={setShowHistoryDropdown}
            >
              <PopoverTrigger asChild>
                <span />
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                sideOffset={8}
                alignOffset={10}
                collisionPadding={16}
                className="p-0 w-96 border-0 shadow-none"
              >
                <div className="w-96 max-h-96 overflow-hidden bg-popover border border-border rounded-md shadow-md">
                  {/* Header section matching the + menu style */}
                  <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium">
                      {t('drawer.multiAskAI.recentSessions', 'Recent Sessions')}
                    </h3>
                  </div>

                  {/* Content area in ScrollArea */}
                  <ScrollArea className="h-64">
                    {(() => {
                      if (isLoadingSessions) {
                        return (
                          <div className="flex items-center justify-center py-8">
                            <Loader className="animate-spin h-5 w-5 text-primary" />
                          </div>
                        );
                      }

                      if (savedConversations.length === 0) {
                        return (
                          <div className="flex items-center justify-center h-full p-8">
                            <p className="text-sm text-muted-foreground text-center">
                              {t(
                                'drawer.multiAskAI.noHistory',
                                'No history available yet',
                              )}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div>
                          {savedConversations.map((conv) => {
                            // Get the first user message as the title
                            const firstUserMsg = conv.conversation.find(
                              (msg) => msg.role === 'user',
                            );
                            const title =
                              firstUserMsg?.content.slice(0, 50) || 'Untitled';

                            const timeAgo = (() => {
                              const diff =
                                Date.now() - new Date(conv.updatedAt).getTime();
                              const minutes = Math.floor(diff / 60000);
                              const hours = Math.floor(diff / 3600000);
                              const days = Math.floor(diff / 86400000);

                              if (minutes < 1) return 'Just now';
                              if (minutes < 60) return `${minutes}m ago`;
                              if (hours < 24) return `${hours}h ago`;
                              return `${days}d ago`;
                            })();

                            return (
                              <button
                                key={conv._id}
                                type="button"
                                onClick={() => loadConversation(conv._id)}
                                className="w-full text-left group flex items-center gap-3 px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors relative"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm mb-1 line-clamp-1 font-normal">
                                    {title}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>
                                      {conv.fileIds.length}{' '}
                                      {conv.fileIds.length === 1
                                        ? 'file'
                                        : 'files'}
                                    </span>
                                    <span>•</span>
                                    <span>{timeAgo}</span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) =>
                                    deleteConversation(conv._id, e)
                                  }
                                  className="opacity-0 group-hover:opacity-70 hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </ScrollArea>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Selected Files Dropdown menu - positioned above the file chip */}
          <Popover
            open={showSelectedFilesDropdown}
            onOpenChange={setShowSelectedFilesDropdown}
          >
            <PopoverTrigger asChild>
              <span />
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              sideOffset={8}
              alignOffset={20}
              collisionPadding={16}
              className="p-0 w-80 border-0 shadow-none"
            >
              <div className="w-80 max-h-80 overflow-hidden bg-popover border border-border rounded-md shadow-md">
                {/* Header section matching the + menu style */}
                <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium">
                    {t('drawer.multiAskAI.selectedFiles', 'Selected Files')}
                  </h3>
                </div>

                {/* Content area in ScrollArea */}
                <ScrollArea className="max-h-64">
                  <div>
                    {cachedFiles.map((file) => (
                      <button
                        key={file._id}
                        type="button"
                        onClick={() => handleFileSelect(file)}
                        className="w-full text-left group flex items-center gap-3 px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors relative"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm line-clamp-1 font-normal">
                            {file.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileSelect(file);
                          }}
                          className="opacity-0 group-hover:opacity-70 hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </PopoverContent>
          </Popover>
        </ChatInput>
      );
    };

    return open ? (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="relative w-full h-full"
      >
        <ChatContainer layout="fullscreen">
          <ChatMessages
            completion={completion}
            isLoading={isLoading}
            stage={stage}
            loadingStage={loadingStage}
            files={{ data: cachedFiles }}
            hideReferences={false}
            resultSearchTerm={resultSearchTerm}
            setResultSearchTerm={setResultSearchTerm}
            onCopyContent={handleCopyContent}
            copySuccess={copySuccess}
            conversationHistory={conversationHistory}
            hideToolbar={false}
            onClearCompletion={clearChat}
            onEditQuery={handleEditQuery}
            onRegenerateResponse={handleRegenerateResponse}
            isFileDragging={isFileDragging}
            hasFiles={selectedFiles.length > 0}
            hasInputText={directions.trim().length > 0}
            onAddFilesClick={() => {
              // Close history dropdown if it's open
              if (showHistoryDropdown) {
                setShowHistoryDropdown(false);
              }
              setShowFileDropdown(true);
            }}
            isLoadingFromHistory={isLoadingFromHistory}
          />
          <ChatContainer.Input>{renderInputArea()}</ChatContainer.Input>
        </ChatContainer>
      </div>
    ) : null;
  },
);

MultiFileChat.displayName = 'MultiFileChat';

export default MultiFileChat;

// Page wrapper component for use in routes
export const MultiFileChatPage: React.FC = () => {
  const [isOpen] = useState(true);

  return (
    <div className="flex w-[calc(100%-32px)] h-[calc(100vh-64px-16px)] m-[8px_16px_8px_16px] mt-0 relative top-0 bg-background rounded-2xl overflow-hidden">
      <MultiFileChat open={isOpen} />
    </div>
  );
};
